import { useState, type ChangeEvent, type KeyboardEvent } from "react";
import { useIntl } from "react-intl";

// Inline pill stepper for a single numeric value. ARIA spinbutton with:
//   − click / ↓ key:     decrement by `step`
//   + click / ↑ key:     increment by `step`
//   Home / End:          jump to `min` / `max` (only when bounded)
//   click value + type:  edit directly; commit on blur / Enter
//
// Use for a numeric input where the range is small or open-ended and the
// user wants both quick ±1 adjustments AND the ability to type a larger
// value directly. For "increment/decrement a value displayed elsewhere",
// use two <Button variant="icon"> instead — that's a different pattern.

interface NumberStepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /**
   * Nachkommastellen der ANZEIGE. Ohne Angabe (Default 0) verhaelt sich der
   * Stepper wie bisher und zeigt den Wert roh. Mit `decimals` wird er auf so
   * viele Stellen formatiert und im aktiven Locale geschrieben (de: Komma).
   * Die Eingabe nimmt weiterhin beides an, Komma wie Punkt.
   */
  decimals?: number;
  label?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function NumberStepper({
  value,
  onChange,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
  step = 1,
  decimals = 0,
  label,
  disabled = false,
  ariaLabel,
  className = "",
}: NumberStepperProps) {
  const intl = useIntl();
  const [draft, setDraft] = useState<string | null>(null);

  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const setClamped = (n: number) => {
    if (Number.isFinite(n)) onChange(clamp(n));
  };

  const handleDecrement = () => setClamped(value - step);
  const handleIncrement = () => setClamped(value + step);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
  };

  const commitDraft = () => {
    if (draft == null) return;
    if (draft.trim() === "") {
      setDraft(null);
      return;
    }
    // Dezimal-Komma (de) wie Punkt behandeln: "6,5" -> 6.5.
    const parsed = Number(draft.replace(",", "."));
    if (Number.isFinite(parsed)) setClamped(parsed);
    setDraft(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      handleIncrement();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleDecrement();
    } else if (e.key === "Home" && Number.isFinite(min)) {
      e.preventDefault();
      setClamped(min);
    } else if (e.key === "End" && Number.isFinite(max)) {
      e.preventDefault();
      setClamped(max);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitDraft();
      (e.currentTarget as HTMLInputElement).blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setDraft(null);
      (e.currentTarget as HTMLInputElement).blur();
    }
  };

  // Nur die Anzeige, nie der gespeicherte Wert. Ohne Gruppierung, weil ein
  // Tausenderpunkt beim Zurueckparsen als Dezimaltrenner missverstanden wuerde.
  const formatted = (n: number) =>
    decimals > 0
      ? new Intl.NumberFormat(intl.locale, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
          useGrouping: false,
        }).format(n)
      : String(n);

  const canDecrement = !disabled && value > min;
  const canIncrement = !disabled && value < max;
  // Screen-reader labels for the ± buttons. The noun comes from the
  // caller (ariaLabel/label is already localized by the consumer); the
  // verb wrapper is localized here so "Decrease …" / "Increase …"
  // matches the rest of the UI locale rather than mixing English verbs
  // with a German noun.
  const noun = ariaLabel ?? label ?? intl.formatMessage({
    id: "design.numberStepperFallbackNoun",
    defaultMessage: "value",
  });
  const decreaseLabel = intl.formatMessage(
    { id: "design.numberStepperDecrease", defaultMessage: "Decrease {noun}" },
    { noun },
  );
  const increaseLabel = intl.formatMessage(
    { id: "design.numberStepperIncrease", defaultMessage: "Increase {noun}" },
    { noun },
  );

  return (
    <div className={className}>
      {label ? (
        <div className="mb-1 text-[10px] uppercase tracking-[0.2em] text-ink/45">{label}</div>
      ) : null}
      <div className="inline-flex items-center overflow-hidden rounded-full ring-1 ring-ink/15 transition focus-within:ring-2 focus-within:ring-ink/30">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={!canDecrement}
          aria-label={decreaseLabel}
          // Mit Nachkommastellen beansprucht das Feld die Breite, also geben die
          // Buttons welche ab. Ganzzahlige Stepper bleiben bei px-2.5.
          className={`${decimals > 0 ? "px-2" : "px-2.5"} py-0.5 text-sm disabled:opacity-30`}
        >
          <span aria-hidden="true">−</span>
        </button>
        <input
          type="text"
          inputMode={decimals > 0 ? "decimal" : "numeric"}
          pattern={decimals > 0 ? "[0-9]*[.,]?[0-9]*" : "[0-9]*"}
          role="spinbutton"
          aria-label={ariaLabel ?? label}
          aria-valuemin={Number.isFinite(min) ? min : undefined}
          aria-valuemax={Number.isFinite(max) ? max : undefined}
          aria-valuenow={value}
          value={draft ?? formatted(value)}
          onChange={handleInputChange}
          onBlur={commitDraft}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          // Feldbreite folgt den Nachkommastellen: w-10 fasst genau vier
          // Zeichen, "10,00" hat fuenf und wurde zu "10,0" abgeschnitten. w-14
          // reicht dafuer und laesst die Pille schmal genug, um zu fuenft in
          // eine Kachelreihe zu passen.
          className={`${decimals > 0 ? "w-14" : "w-10"} bg-transparent px-1 py-0.5 text-center text-sm font-medium tabular-nums outline-none disabled:opacity-50`}
        />
        <button
          type="button"
          onClick={handleIncrement}
          disabled={!canIncrement}
          aria-label={increaseLabel}
          className={`${decimals > 0 ? "px-2" : "px-2.5"} py-0.5 text-sm disabled:opacity-30`}
        >
          <span aria-hidden="true">+</span>
        </button>
      </div>
    </div>
  );
}
